import { makeRows } from '@/common/utils/gridUtils';
import { columnFormatter, filterDeletedTag } from '@/alert/utils/utils';
import { AlertTargetItem } from '@/alert/components/alertTargetList/alertTargetList.types';
import { CustomColumn, CustomColumnsReadonly } from '@/common/utils/types';
import { AlertType } from '@/alert/components/alertDetail/alertDetail.types';
import { isDateTime } from '@/common/utils/time.utils';
import { utcZeroTimeToStandardTime } from '@/common/utils/commonUtils';
import { ASTER_KEY } from '@/dashboard/features/widgets/common/constants/define';
import { isK8sLoggingAlertCategory } from '@/config/views/userAlert/alertRuleWindow.uses';
import { UserAlertType } from '@/config/views/userAlert/alertRuleWindow.types';
import { useTargetTagStore } from '@/common/stores/target-tags';
import { SERVER_TAG_SEPARATOR } from '@/common/define/common.define';
import { getDisplayName } from '@/common/utils/core/string/displayName';
import type { TargetItem } from '@/api-client/metav8Over/model/target-item';

export const TARGET_TYPE = {
  ALL: 'all',
  TARGETS: 'targets',
  TAGS: 'tags',
  NONE: 'none',
} as const;

export const ALERT_RULE_NAME_ICON = 'icon-child-right';

interface MakeAlertRowsParam {
  data: any[];
  columns: CustomColumn[] | CustomColumnsReadonly;
  targetFieldName?: string;
  targetType?: (typeof TARGET_TYPE)[keyof typeof TARGET_TYPE];
  formatter?: { [key in string]?: (v, item) => any };
  alertType?: AlertType;
  hasNullRow?: boolean;
}

interface TagItem {
  tagId: string;
  tagName: string;
  tagKey: string;
}

interface TagsAndTargetsOts {
  type?: Capitalize<UserAlertType>;
  predicateTag?: (tagItem: TagItem) => boolean;
}

const getItems = {
  tags: (
    items: TagItem[],
    { type, predicateTag }: TagsAndTargetsOts = {},
  ): { tags: AlertTargetItem[] } => {
    if (!items?.length || !Array.isArray(items)) {
      return { tags: [] };
    }

    const formatName = ({ tagKey, tagName, tagId }: TagItem) => {
      const isK8sEventLoggingAlert =
        type === 'Event' && isK8sLoggingAlertCategory({ category: tagKey });
      const showCategory = !isK8sEventLoggingAlert;
      const convertedTagKey = tagKey.replace(SERVER_TAG_SEPARATOR, '');

      if (showCategory) {
        return `${convertedTagKey}:${tagName || tagId || ''}`;
      }

      if (tagId === ASTER_KEY) {
        return `${convertedTagKey}:${tagId}`;
      }

      return `${tagName || tagId || ''}`;
    };

    return {
      tags: Array.from(
        new Map(
          items
            .filter((item) => {
              if (predicateTag) {
                return filterDeletedTag(item) && predicateTag(item);
              }

              return filterDeletedTag(item);
            })
            .map((item) => [
              `${item.tagKey}${item.tagId}`,
              {
                id: item.tagId,
                name: formatName(item),
                category: item.tagKey,
              },
            ]),
        ).values(),
      ),
    };
  },
  targets: (items: TargetItem[]): { targets: AlertTargetItem[] } => {
    if (!items?.length || !Array.isArray(items)) {
      return { targets: [] };
    }

    const formatName = ({ category, targetAlias, targetName, targetId }: TargetItem) => {
      const showCategory = category !== 'process';
      const displayName = getDisplayName(targetName ?? '', targetAlias) || targetId;

      if (showCategory) {
        return `${category}:${displayName}`;
      }

      return `${displayName}`;
    };
    return {
      targets: Array.from(
        new Map(
          items.map((item) => {
            return [
              `${item.category}${item.targetId}`,
              {
                id: item.targetId,
                name: formatName(item),
                alias: item.targetAlias,
                category: item.category,
              },
            ];
          }),
        ).values(),
      ),
    };
  },
};

export const tagsAndTargetsController = {
  [TARGET_TYPE.TARGETS]: (data) => getItems.targets(data),
  [TARGET_TYPE.TAGS]: (data, options: TagsAndTargetsOts = {}) => getItems.tags(data, options),
  [TARGET_TYPE.ALL]: (data, options: TagsAndTargetsOts = {}) => ({
    ...getItems.targets(
      data
        ?.map((item) => item?.targetItems)
        .flat()
        .filter((item) => !!item),
    ),
    ...getItems.tags(
      data
        ?.map((item) => item?.tagItems)
        .flat()
        .filter((item) => !!item),
      options,
    ),
  }),
};

export const useAlertTagsAndTargets = () => {
  const { getTagMapValue } = useTargetTagStore();

  const makeAlertRows = ({
    data,
    columns,
    targetFieldName = 'targets',
    targetType = TARGET_TYPE.ALL,
    formatter = {},
    hasNullRow = false,
  }: MakeAlertRowsParam) => {
    if (!data || !columns) {
      return [];
    }
    let alertData: any[] = data;
    if (hasNullRow) {
      alertData = alertData.filter((item) => !!item);
    }
    alertData = alertData.map((item) => {
      // TODO targetServer check 타겟서버 확인
      const tagsAndTargets =
        targetType === TARGET_TYPE.NONE
          ? {}
          : tagsAndTargetsController[targetType](item[targetFieldName], {
              type: item.type,
              predicateTag: (tag: TagItem) =>
                !!getTagMapValue({ id: tag?.tagId, category: tag?.tagKey }),
            });
      return {
        ...item,
        ...tagsAndTargets,
      };
    });

    const mergedFormatter = {
      ...columnFormatter,
      ...formatter,
    };
    return makeRows(alertData, columns, mergedFormatter);
  };

  const makeAlertTreeRows = ({
    data,
    columns,
    targetFieldName = 'targets',
    targetType = TARGET_TYPE.ALL,
    formatter = {},
    hasNullRow = false,
  }: MakeAlertRowsParam) => {
    if (!data?.length) {
      return [];
    }
    let alertData: any[] = data;
    if (hasNullRow) {
      alertData = alertData.filter((item) => !!item);
    }

    const mergedFormatter = {
      ...columnFormatter,
      ...formatter,
    };

    return alertData.map((item) => {
      const formattedItem = columns.reduce(
        (acc, cur: CustomColumn) => {
          const { field } = cur;
          acc[field] = item[field];

          if (isDateTime(acc[field])) {
            acc[field] = utcZeroTimeToStandardTime(acc[field]);
          }

          if (mergedFormatter[field]) {
            acc[field] = mergedFormatter[field]!(acc[field], item);
          }

          return acc;
        },
        // @ts-expect-error TODO: FIXME
        {} as Record<string, any>,
      );

      const tagsAndTargets =
        targetType === TARGET_TYPE.NONE
          ? {}
          : tagsAndTargetsController[targetType](item[targetFieldName]);

      return {
        ...formattedItem,
        ...tagsAndTargets,
        subType: item.subType,
        ruleCriteria: item.ruleCriteria,
        children: item.child?.length
          ? makeAlertTreeRows({
              data: item.child.map((childItem) => ({
                ...childItem,
                ...(childItem.threshold ?? {}),
                parent: item,
                alertName: childItem.threshold?.alertName || childItem.alertName,
              })),
              columns,
              targetFieldName,
              targetType,
              formatter,
              hasNullRow,
            }).map((childItem) => ({
              ...childItem,
              alertRuleName: ALERT_RULE_NAME_ICON,
              uncheckable: true,
              isChild: true,
              children: undefined,
            }))
          : undefined,
      };
    });
  };

  return {
    tagsAndTargetsController,
    makeAlertRows,
    makeAlertTreeRows,
  };
};
